import {
  useReducer,
  useContext,
  createContext,
  useEffect,
  useCallback,
} from "react";
import { useAuth } from "./AuthContext";

const baseUrl = process.env.REACT_APP_BASE_URL;

const TagsContext = createContext();

const initialState = {
  tags: [],
  tagIsLoading: false,
  currenttag: [],
  error: "",
};

function reducer(state, action) {
  switch (action.type) {
    case "loading":
      return { ...state, tagIsLoading: true, error: "" };
    case "tags/loaded":
      return { ...state, tagIsLoading: false, tags: action.payload, error: "" };
    case "tag/loaded":
      return {
        ...state,
        tagIsLoading: false,
        currenttag: action.payload,
        error: "",
      };
    case "tag/created":
      // Deduplicate new tags by name
      const existingNames = new Set(
        state.tags.map((tag) => tag.name.toLowerCase())
      );
      const newTags = (action.payload.newTags || []).filter(
        (tag) => !existingNames.has(tag.name.toLowerCase())
      );
      return {
        ...state,
        tagIsLoading: false,
        tags: [...state.tags, ...newTags],
        currenttag: action.payload.tags,
        error: "",
      };
    case "tag/updated":
      return {
        ...state,
        tagIsLoading: false,
        tags: state.tags.map((tag) =>
          tag.id === Number(action.payload.tagID)
            ? {
                id: Number(action.payload.tagID),
                name: action.payload.tagTitle,
              }
            : tag
        ),
        currenttag: [],
      };
    case "tag/deleted":
      return {
        ...state,
        tagIsLoading: false,
        tags: state.tags.filter((tag) => tag.id !== Number(action.payload)),
        currenttag: [],
      };
    case "reset":
      return initialState;
    case "rejected":
      return { ...state, tagIsLoading: false, error: action.payload };
    default:
      throw new Error("Unknown action type");
  }
}

function TagsProvider({ children }) {
  const { user, isLoading, fetchWithToken } = useAuth(); // Removed logout
  const [{ tags, tagIsLoading, currenttag, error }, dispatch] = useReducer(
    reducer,
    initialState
  );

  // Debug state updates
  useEffect(() => {
    if (isLoading || !user?.CustUUID) {
      console.log("TagsContext: Waiting for user authentication");
      return;
    }

    console.log("TagsContext: user:", user);
    console.log("TagsContext: tags:", tags);
    console.log("TagsContext: error:", error);
  }, [user, tags, error]);

  // Fetch all tags
  const fetchTags = useCallback(async () => {
    if (!user?.userId) {
      console.log("fetchTags: No userId available");
      dispatch({ type: "rejected", payload: "Please log in to view tags" });
      return;
    }
    dispatch({ type: "loading" });
    try {
      console.log(`fetchTags: Fetching tags for userId=${user.userId}`);
      const res = await fetchWithToken(`${baseUrl}/tags/${user.userId}`);
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message || `HTTP ${res.status}`);
      }
      const response = await res.json();
      console.log("fetchTags response:", response);
      if (response.status === "error") throw new Error(response.message);
      // Deduplicate tags by name
      const uniqueTags = [
        ...new Map(
          (response.data || []).map((tag) => [tag.name.toLowerCase(), tag])
        ).values(),
      ];
      dispatch({ type: "tags/loaded", payload: uniqueTags });
    } catch (err) {
      console.error("fetchTags error:", err.message);
      dispatch({
        type: "rejected",
        payload: err.message || "Error loading tags",
      });
    }
  }, [user, fetchWithToken]);

  useEffect(() => {
    if (user?.userId) {
      fetchTags();
    }
  }, [fetchTags, user]);

  // Fetch single tag by tagid
  const getTag = useCallback(
    async (tagid) => {
      if (!user?.userId) {
        dispatch({ type: "rejected", payload: "Please log in to view tag" });
        return;
      }
      dispatch({ type: "loading" });
      try {
        console.log(`getTag: Fetching tagid=${tagid}`);
        const res = await fetchWithToken(
          `${baseUrl}/tags/single/${tagid}/${user.userId}`
        );
        if (!res.ok) {
          const data = await res.json();
          throw new Error(data.message || `HTTP ${res.status}`);
        }
        const response = await res.json();
        console.log("getTag response:", response);
        if (response.status === "error") throw new Error(response.message);
        dispatch({
          type: "tag/loaded",
          payload: [response.data], // Wrap in array to match currenttag
        });
      } catch (err) {
        console.error("getTag error:", err.message);
        dispatch({
          type: "rejected",
          payload: err.message || "Error loading tag",
        });
      }
    },
    [user, fetchWithToken]
  );

  const getTags = useCallback(
    async (tagtype, itemid) => {
      if (!user?.userId) {
        dispatch({ type: "rejected", payload: "Please log in to view tags" });
        return;
      }
      dispatch({ type: "loading" });
      try {
        console.log(
          `getTags: Fetching tags for tagtype=${tagtype}, itemid=${itemid}`
        );
        const res = await fetchWithToken(
          `${baseUrl}/tags/${tagtype}/${itemid}/${user.userId}`
        );
        if (!res.ok) {
          const data = await res.json();
          throw new Error(data.message || `HTTP ${res.status}`);
        }
        const response = await res.json();
        console.log("getTags response:", response);
        if (response.status === "error") throw new Error(response.message);
        // Deduplicate tags by name
        const uniqueTags = [
          ...new Map(
            (response.data || []).map((tag) => [tag.name.toLowerCase(), tag])
          ).values(),
        ];
        dispatch({
          type: "tag/loaded",
          payload: uniqueTags,
        });
      } catch (err) {
        console.error("getTags error:", err.message);
        dispatch({
          type: "rejected",
          payload: err.message || "Error loading item tags",
        });
      }
    },
    [user, fetchWithToken]
  );

  const createtag = useCallback(
    async (newtags, tagtype, itemid) => {
      if (!user?.userId) {
        dispatch({ type: "rejected", payload: "Please log in to create tags" });
        throw new Error("No userId available");
      }
      dispatch({ type: "loading" });
      try {
        // Format tags, preserving tagId if provided
        const formattedTags = newtags.map((tag) => ({
          name: tag.name || tag.tagTitle, // Handle both formats
          id: tag.id || tag.tagId || undefined,
        }));
        console.log(
          `createtag: Creating tags for tagtype=${tagtype}, itemid=${itemid}`,
          formattedTags
        );
        const res = await fetchWithToken(
          `${baseUrl}/tags/${tagtype}/${itemid}/${user.userId}`,
          {
            method: "POST",
            body: JSON.stringify(formattedTags),
          }
        );
        if (!res.ok) {
          const data = await res.json();
          throw new Error(data.message || `HTTP ${res.status}`);
        }
        const response = await res.json();
        console.log("createtag response:", response);
        if (response.status === "error") throw new Error(response.message);
        dispatch({
          type: "tag/created",
          payload: {
            tags: formattedTags,
            newTags: response.data?.newTags || [],
          },
        });
        return response.data;
      } catch (err) {
        console.error("createtag error:", err.message);
        dispatch({
          type: "rejected",
          payload: err.message || "Error creating tags",
        });
        throw new Error(err.message || "Error creating tags");
      }
    },
    [user, fetchWithToken]
  );

  const updatetag = useCallback(
    async (thetag, tagid) => {
      if (!user?.userId) {
        dispatch({ type: "rejected", payload: "Please log in to update tags" });
        return;
      }
      dispatch({ type: "loading" });
      try {
        console.log(`updatetag: Updating tagid=${tagid}, tagTitle=${thetag}`);
        const res = await fetchWithToken(
          `${baseUrl}/tags/update-tag/${tagid}/${user.userId}`,
          {
            method: "POST",
            body: JSON.stringify({ thetag }),
          }
        );
        if (!res.ok) {
          const data = await res.json();
          throw new Error(data.message || `HTTP ${res.status}`);
        }
        const response = await res.json();
        console.log("updatetag response:", response);
        if (response.status === "error") throw new Error(response.message);
        dispatch({ type: "tag/updated", payload: response.data || response });
      } catch (err) {
        console.error("updatetag error:", err.message);
        dispatch({
          type: "rejected",
          payload: err.message || "Error updating tag",
        });
      }
    },
    [user, fetchWithToken]
  );

  const deletetag = useCallback(
    async (tagid) => {
      if (!user?.userId) {
        dispatch({ type: "rejected", payload: "Please log in to delete tags" });
        return;
      }
      dispatch({ type: "loading" });
      try {
        console.log(`deletetag: Deleting tagid=${tagid}`);
        const res = await fetchWithToken(
          `${baseUrl}/tags/${tagid}/${user.userId}`,
          {
            method: "DELETE",
          }
        );
        if (!res.ok) {
          const data = await res.json();
          throw new Error(data.message || `HTTP ${res.status}`);
        }
        console.log("deletetag response: Success");
        dispatch({ type: "tag/deleted", payload: tagid });
      } catch (err) {
        console.error("deletetag error:", err.message);
        dispatch({
          type: "rejected",
          payload: err.message || "Error deleting tag",
        });
      }
    },
    [user, fetchWithToken]
  );

  const resetTags = useCallback(() => {
    dispatch({ type: "reset" });
  }, []);

  return (
    <TagsContext.Provider
      value={{
        tags,
        tagIsLoading,
        currenttag,
        error,
        getTag,
        getTags,
        createtag,
        updatetag,
        deletetag,
        resetTags,
      }}
    >
      {children}
    </TagsContext.Provider>
  );
}

function useTags() {
  const context = useContext(TagsContext);
  if (context === undefined) {
    throw new Error("TagsContext used outside of TagsProvider");
  }
  return context;
}

export { TagsProvider, useTags };
